import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import moment from 'moment';

import { FHSimpleTable } from '../../../base';
import {
  btSNHistoryDataTransformer,
  clientSNHistoryDataTransformer,
  groupSNHistoryDataTransformer,
  parentSNHistoryDataTransformer,
} from '../utils';
import { history } from '../../../../helpers';
import {
  getHistoryTableOptions,
  clientAndBtSNHistoryColumns,
  parentSNHistoryColumns,
} from '../table-configs';
import { HistoricalDocuments } from '../../../../pages/constants';
import { groupSNHistoryColumns } from '../table-configs/supervisionTableConfig';

import type { SupervisionNoteStatus } from '../../../../constants';
import type {
  IClientSupervision,
  IParentSupervision,
  IBtSupervision,
  IGroupSupervision,
} from '../../../../utils/types';

type SupervisionHistoryTableProps =
  | {
      data: IClientSupervision[];
      documentType: HistoricalDocuments.ClientSupervision;
    }
  | {
      data: IBtSupervision[];
      documentType: HistoricalDocuments.BTSupervision;
    }
  | {
      data: IParentSupervision[];
      documentType: HistoricalDocuments.ParentSupervision;
    }
  | {
      data: IGroupSupervision[];
      documentType: HistoricalDocuments.GroupSupervision;
    };

interface ISNHistoryTableData {
  id?: number;
  status?: SupervisionNoteStatus;
  client: string;
  bt?: string;
  session_id: string;
  date: string;
  time: string;
  duration: string;
}

function SupervisionHistoryTable({ data, documentType }: SupervisionHistoryTableProps) {
  const tableOptions = useMemo(() => {
    return {
      ...getHistoryTableOptions(documentType),
      onRowClick: (args: any) => {
        switch (documentType) {
          case HistoricalDocuments.BTSupervision:
            history.navigate(`/bt-supervisions/${args[0]}`);
            break;
          case HistoricalDocuments.ClientSupervision:
            history.navigate(`/client-supervisions/${args[0]}`);
            break;
          case HistoricalDocuments.GroupSupervision:
            history.navigate(`/group-parent-training/${args[0]}`);
            break;
          case HistoricalDocuments.ParentSupervision:
            history.navigate(`/parent-supervisions/${args[0]}`);
            break;
          default:
            return;
        }
      },
    };
  }, [documentType]);

  const tableColumns = useMemo(() => {
    let config;
    switch (documentType) {
      case HistoricalDocuments.BTSupervision:
      case HistoricalDocuments.ClientSupervision:
        config = clientAndBtSNHistoryColumns;
        break;
      case HistoricalDocuments.GroupSupervision:
        config = groupSNHistoryColumns;
        break;
      case HistoricalDocuments.ParentSupervision:
        config = parentSNHistoryColumns;
        break;
      default:
        config = clientAndBtSNHistoryColumns;
    }
    return config;
  }, [documentType]);

  const [historyTableData, setHistoryTableData] = useState<ISNHistoryTableData[]>([]);

  useEffect(() => {
    let transformedTableData: ISNHistoryTableData[] = [];
    if (documentType === HistoricalDocuments.ClientSupervision) {
      transformedTableData = clientSNHistoryDataTransformer(data);
    } else if (documentType === HistoricalDocuments.BTSupervision) {
      transformedTableData = btSNHistoryDataTransformer(data);
    } else if (documentType === HistoricalDocuments.ParentSupervision) {
      transformedTableData = parentSNHistoryDataTransformer(data);
    } else if (documentType === HistoricalDocuments.GroupSupervision) {
      transformedTableData = groupSNHistoryDataTransformer(data);
    }

    transformedTableData.sort((a: ISNHistoryTableData, b: ISNHistoryTableData) => {
      if (moment(a.date).isBefore(moment(b.date))) {
        return 1;
      } else if (moment(a.date).isAfter(moment(a.date))) {
        return -1;
      }
      return 0;
    });
    setHistoryTableData(transformedTableData);
  }, [data]);

  return (
    <Box data-testid="historicalSNTable">
      <FHSimpleTable
        data={historyTableData}
        columnDefinitions={tableColumns}
        columnOptions={tableOptions}
      />
    </Box>
  );
}

export default SupervisionHistoryTable;
